import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import ScrollToTop from './components/ScrollToTop';

// ─── Contexts ────────────────────────────────────────────
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { MessageProvider } from './contexts/MessageContext';

// ─── Components ──────────────────────────────────────────
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AuthCallback from './components/AuthCallback';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import MaintenanceBanner from './components/MaintenanceBanner';
import MyData from './components/MyData';
import BloodTestAnalysis from './components/BloodTestAnalysis';

// ─── Pages : Auth & Public ───────────────────────────────
import AuthPage from './pages/AuthPage';
import AuthProPage from './pages/pro/AuthProPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PublicArticlesListPage from './pages/PublicArticlesListPage';
import PublicArticlePage from './pages/PublicArticlePage';
import PublicAboutPage from './pages/PublicAboutPage';
import PublicFAQPage from './pages/PublicFAQPage';
import PublicPricingPage from './pages/PublicPricingPage';
import Feedback from './pages/Feedback';
import WhatsNew from './pages/WhatsNew';
import EmailVerificationPage from './pages/EmailVerificationPage';



// ─── Pages : Protected ──────────────────────────────────
import Dashboard from './pages/Dashboard';
import MedicationsPage from './pages/MedicationsPage';
import SymptomsPage from './pages/SymptomsPage';
import DailyHelpPage from './pages/DailyHelpPage';
import Pricing from './pages/Pricing';
import ForumPage from './pages/ForumPage';
import ThreadDetailPage from './pages/ThreadDetailPage';
import EmergencyPage from './pages/EmergencyPage';
import PharmacyPage from './pages/PharmacyPage';
import HistoryPage from './pages/HistoryPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import QuizPage from './pages/QuizPage';
import HealthAlertsPage from './pages/HealthAlertsPage';
import PilulierPage from './pages/PilulierPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import HealthRecordPage from './pages/HealthRecordPage';

// ─── Pages : Pro ─────────────────────────────────────────
import ChatInfirmierPage from './pages/pro/ChatInfirmierPage';
import CalculateursInfPage from './pages/pro/CalculateursInfPage';
import ScoresCliniquesPage from './pages/pro/ScoresCliniquesPage';
import MedicamentProPage from './pages/pro/MedicamentProPage';
import DashboardInfirmier from './pages/pro/DashboardInfirmier';
import GestionComptePage from './pages/pro/GestionComptePage';
import FaqProPage from './pages/pro/FaqProPage';
import MonProfilPage from './pages/pro/MonProfilPage';
import ProtocolesInfPage from './pages/pro/ProtocolesInfPage';
import ProfessionalRoute from './components/ProfessionalRoute';

// ─── Pages : Admin ───────────────────────────────────────
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminArticlesPage from './pages/AdminArticlesPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminMessagesPage from './pages/AdminMessagesPage';

// ─── Pages : Messages ────────────────────────────────────
import MessagesPage from './pages/MessagesPage';

// ─── Pages : Legal ───────────────────────────────────────
import CGU from './pages/legal/CGU';
import Privacy from './pages/legal/Privacy';
import Mentions from './pages/legal/Mentions';
import Disclaimer from './pages/legal/Disclaimer';
import Cookies from './pages/legal/Cookies';
import Support from './pages/legal/Support';

// ─── Styles ──────────────────────────────────────────────
import './App.css';
import './styles/article.css';


// ═════════════════════════════════════════════════════════
//  ROUTER
// ═════════════════════════════════════════════════════════

function AppRouter() {
  const location = useLocation();

  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <MaintenanceBanner />
      <ScrollToTop />
      <main className="flex-grow">
        
        
         <Routes>
    {/* ── Public ─────────────────────────────── */}
    <Route path="/" element={<AuthPage />} />
    <Route path="/auth" element={<AuthPage />} />
    <Route path="/auth/pro" element={<AuthProPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/articles" element={<PublicArticlesListPage />} />
    <Route path="/articles/:articleId" element={<PublicArticlePage />} />
    <Route path="/faq" element={<PublicFAQPage />} />
    <Route path="/feedback" element={<Feedback />} />
    <Route path="/whats-new" element={<WhatsNew />} />
    <Route path="/about" element={<PublicAboutPage />} />
    <Route path="/tarifs" element={<PublicPricingPage />} />
    <Route path="/verify-email" element={<EmailVerificationPage />} />

    {/* ── Legal ──────────────────────────────── */}
    <Route path="/legal/cgu" element={<CGU />} />
    <Route path="/legal/privacy" element={<Privacy />} />
    <Route path="/legal/mentions" element={<Mentions />} />
    <Route path="/legal/disclaimer" element={<Disclaimer />} />
    <Route path="/legal/cookies" element={<Cookies />} />
    <Route path="/support" element={<Support />} />
    <Route path="/contact" element={<Support />} />

    {/* ── Protected ──────────────────────────── */}
    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    <Route path="/medications" element={<ProtectedRoute><MedicationsPage /></ProtectedRoute>} />
    <Route path="/symptoms" element={<ProtectedRoute><SymptomsPage /></ProtectedRoute>} />
    <Route path="/blood-test-analysis" element={<ProtectedRoute><BloodTestAnalysis /></ProtectedRoute>} />
    <Route path="/daily-help" element={<ProtectedRoute><DailyHelpPage /></ProtectedRoute>} />
    <Route path="/daily-help/article/:articleId" element={<ArticleDetailPage />} />
    <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
    <Route path="/forum" element={<ProtectedRoute><ForumPage /></ProtectedRoute>} />
    <Route path="/forum/thread/:threadId" element={<ProtectedRoute><ThreadDetailPage /></ProtectedRoute>} />
    <Route path="/emergency" element={<ProtectedRoute><EmergencyPage /></ProtectedRoute>} />
    <Route path="/pharmacy" element={<ProtectedRoute><PharmacyPage /></ProtectedRoute>} />
    <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
    <Route path="/evaluation" element={<ProtectedRoute><QuizPage /></ProtectedRoute>} />
    <Route path="/alertes-sante" element={<ProtectedRoute><HealthAlertsPage /></ProtectedRoute>} />
    <Route path="/pilulier" element={<ProtectedRoute><PilulierPage /></ProtectedRoute>} />
    <Route path="/my-data" element={<ProtectedRoute><MyData /></ProtectedRoute>} />
    <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
    <Route path="/dashboard/about" element={<ProtectedRoute><PublicAboutPage /></ProtectedRoute>} />
    <Route path="/health-record" element={<ProtectedRoute><HealthRecordPage /></ProtectedRoute>} />
    <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />

    {/* ── Pro Infirmier ──────────────────────── */}
    <Route path="/pro/infirmier" element={<ProfessionalRoute><DashboardInfirmier /></ProfessionalRoute>} />
    <Route path="/pro/infirmier/chat" element={<ProfessionalRoute><ChatInfirmierPage /></ProfessionalRoute>} />
    <Route path="/pro/calculateurs" element={<ProfessionalRoute><CalculateursInfPage /></ProfessionalRoute>} />
    <Route path="/pro/scores" element={<ProfessionalRoute><ScoresCliniquesPage /></ProfessionalRoute>} />
    <Route path="/pro/medicaments" element={<ProfessionalRoute><MedicamentProPage /></ProfessionalRoute>} />
    <Route path="/pro/infirmier/compte" element={<ProfessionalRoute><GestionComptePage /></ProfessionalRoute>} />
    <Route path="/pro/infirmier/faq" element={<ProfessionalRoute><FaqProPage /></ProfessionalRoute>} />
    <Route path="/pro/infirmier/profil" element={<ProfessionalRoute><MonProfilPage /></ProfessionalRoute>} />
    <Route path="/pro/protocoles" element={<ProfessionalRoute><ProtocolesInfPage /></ProfessionalRoute>} />

    {/* ── Admin ──────────────────────────────── */}
    <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
    <Route path="/admin/articles" element={<AdminRoute><AdminArticlesPage /></AdminRoute>} />
    <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
    <Route path="/admin/messages" element={<AdminRoute><AdminMessagesPage /></AdminRoute>} />

    {/* ── Catch-all ──────────────────────────── */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
</main>

      <Footer />
      <CookieBanner />
    </div>
  );
}


// ═════════════════════════════════════════════════════════
//  APP
// ═════════════════════════════════════════════════════════

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <MessageProvider>
            <AppRouter />
          </MessageProvider>
          <Toaster position="top-right" duration={2000} richColors />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;